import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import ShoppingBagRounded from '@mui/icons-material/ShoppingBagRounded';
import SimCardRounded from '@mui/icons-material/SimCardRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import VideocamRounded from '@mui/icons-material/VideocamRounded';
import { TFunction } from 'i18next';
import { PRODUCTS, PRODUCT_TYPE_BY_PRODUCT, type TProductName } from 'themes/tokens/productTheme';
import { TNavData_SideBar } from 'typings';
import { getRoute, ROUTES } from 'utils/constants';

type TSideBarT = TFunction<'translation', 'layout.sidebar'>;

/**
 * Product picker labels — i18n keys under `layout.sidebar.products`, resolved
 * with `t()` at the call site (same convention as every other sidebar label).
 */
type TProductLabelKey =
  | 'products.home'
  | 'products.bird'
  | 'products.pet'
  | 'products.baby'
  | 'products.trail';

export const PRODUCT_LABEL_KEY: Record<TProductName, TProductLabelKey> = {
  home: 'products.home',
  bird: 'products.bird',
  pet: 'products.pet',
  baby: 'products.baby',
  trail: 'products.trail'
};

const UPSELL_ROUTE: Record<TProductName, string> = {
  home: ROUTES.PROMOTION,
  bird: ROUTES.BIRD_UPSELL,
  pet: ROUTES.PET_UPSELL,
  baby: ROUTES.BABY_UPSELL,
  trail: ROUTES.TRAIL_CAM_UPSELL
};

const BUY_SUBSCRIPTION_ROUTE: Record<TProductName, string> = {
  home: ROUTES.HOME_BUY_SUBSCRIPTION,
  bird: ROUTES.BIRD_BUY_SUBSCRIPTION,
  pet: ROUTES.PET_BUY_SUBSCRIPTION,
  baby: ROUTES.BABY_BUY_SUBSCRIPTION,
  trail: ROUTES.TRAIL_CAM_BUY_SUBSCRIPTION
};

const SUBSCRIPTION_DETAIL_ROUTE: Record<TProductName, string> = {
  home: ROUTES.SUBSCRIPTION_DETAIL,
  bird: ROUTES.BIRD_SUBSCRIPTION_DETAIL,
  pet: ROUTES.PET_SUBSCRIPTION_DETAIL,
  baby: ROUTES.BABY_SUBSCRIPTION_DETAIL,
  trail: ROUTES.TRAIL_CAM_SUBSCRIPTION_DETAIL
};

export type TProductNavKey =
  | 'upsell'
  | 'dataPlans'
  | 'comboPlans'
  | 'subscriptionDetails'
  | 'devices';

/**
 * The route a given nav row points to for `product` — the one place that
 * knows how each row's link is built, shared by `getProductNavData` (listing
 * the current product's rows) and `getRouteForProductNavKey` (working out
 * where the SAME row lands for a DIFFERENT product, so switching products
 * while on e.g. Home's Upsell page lands on Pet's Upsell page instead of
 * just re-theming the page you're already on).
 */
export const getRouteForProductNavKey = (
  key: TProductNavKey,
  product: TProductName,
  subscriptionId: string | undefined
): string => {
  switch (key) {
    case 'upsell':
      return UPSELL_ROUTE[product];
    case 'comboPlans':
      return BUY_SUBSCRIPTION_ROUTE[product];
    case 'subscriptionDetails':
      return subscriptionId
        ? getRoute(SUBSCRIPTION_DETAIL_ROUTE[product], { id: subscriptionId })
        : BUY_SUBSCRIPTION_ROUTE[product];
    case 'devices':
      return `${ROUTES.DASHBOARD}?product=${PRODUCT_TYPE_BY_PRODUCT[product]}`;
    case 'dataPlans':
    default:
      // Shared across every product — no product segment to swap.
      return ROUTES.DATA_SUBSCRIPTION;
  }
};

/**
 * Which nav row (if any) `pathname` belongs to for `product` — the reverse
 * of `getRouteForProductNavKey`. `null` means the current page has no
 * per-product equivalent (FAQ, My Order, Payment Method, the dashboard's
 * own chrome, …), so switching products there should just re-theme in
 * place rather than navigate anywhere.
 */
export const findProductNavKeyForPath = (
  product: TProductName,
  pathname: string
): TProductNavKey | null => {
  if (pathname.search(UPSELL_ROUTE[product]) >= 0) return 'upsell';
  if (pathname.search(BUY_SUBSCRIPTION_ROUTE[product]) >= 0) return 'comboPlans';
  if (pathname === ROUTES.DASHBOARD) return 'devices';
  if (pathname === ROUTES.DATA_SUBSCRIPTION) return 'dataPlans';
  // Broadest check last: ROUTES.DATA_SUBSCRIPTION ('/data/subscription/buy')
  // also contains this substring, so it must lose to the exact match above.
  if (pathname.search('/subscription') >= 0) return 'subscriptionDetails';
  return null;
};

/**
 * Builds the per-product sub-nav: Upsell, Data plans (only when the account
 * actually has a 4G-capable device of this product — see `hasFourGDevice`),
 * AI/Combo plans, Subscription details, Devices — plus Help & Support / FAQ,
 * My Order and Payment Method, which are global and unaffected by the
 * selected product.
 *
 * `subscriptionId` is the selected product's own active subscription id
 * (looked up in Redux by the caller — SideBar already has that data loaded
 * for other reasons). Without one yet (no purchase for that product), the
 * Subscription details row falls back to that product's own buy-subscription
 * page rather than a `:id` route with nothing to fill it — SUBSCRIPTION_LIST
 * was tried first, but it's HomeSubscriptionListV1, a Home-only redirector
 * that silently sends every other product into Home's subscription/buy flow.
 */
export const getProductNavData = (
  product: TProductName,
  subscriptionId: string | undefined,
  hasFourGDevice: boolean,
  t: TSideBarT
): TNavData_SideBar[] => {
  const items: TNavData_SideBar[] = [
    {
      name: t('upsell'),
      link: getRouteForProductNavKey('upsell', product, subscriptionId),
      macthRoutes: [UPSELL_ROUTE[product]],
      icon: TrendingUpRounded
    }
  ];

  if (hasFourGDevice) {
    items.push({
      name: t('dataPlans'),
      link: getRouteForProductNavKey('dataPlans', product, subscriptionId),
      macthRoutes: [ROUTES.DATA_SUBSCRIPTION],
      icon: SimCardRounded
    });
  }

  items.push(
    {
      name: t('comboPlans'),
      link: getRouteForProductNavKey('comboPlans', product, subscriptionId),
      macthRoutes: [BUY_SUBSCRIPTION_ROUTE[product]],
      icon: AutoAwesomeRounded
    },
    {
      name: t('subscriptionDetails'),
      link: getRouteForProductNavKey(
        'subscriptionDetails',
        product,
        subscriptionId
      ),
      macthRoutes: ['/subscription'],
      icon: ReceiptLongRounded
    },
    {
      name: t('devices'),
      link: getRouteForProductNavKey('devices', product, subscriptionId),
      macthRoutes: [ROUTES.DASHBOARD],
      icon: VideocamRounded
    }
  );

  return items;
};

export const getGlobalNavData = (t: TSideBarT): TNavData_SideBar[] => [
  {
    name: t('myOrder'),
    link: ROUTES.BILLINGS,
    macthRoutes: [ROUTES.BILLINGS],
    icon: ShoppingBagRounded
  },
  {
    name: t('paymentMethod'),
    link: ROUTES.PAYMENT_METHODS,
    macthRoutes: [ROUTES.PAYMENT_METHODS],
    icon: CreditCardRounded
  },
  {
    name: t('helpAndSupport'),
    link: ROUTES.SUPPORT,
    macthRoutes: [ROUTES.SUPPORT],
    icon: HelpOutlineRounded
  },
  {
    name: t('faq'),
    link: ROUTES.FAQ,
    macthRoutes: [ROUTES.FAQ],
    icon: ChatBubbleOutlineRounded
  }
];

export { PRODUCTS };
